/* ==========================================================================
   nb-widgets.js — Nuttet Baby web components
   Porteret fra design/nb-widgets.js. Tre custom elements:

     <nb-countdown>     fragt-cutoff-nedtaelling + estimeret leveringsdato
     <nb-email-popup>   nyhedsbrevs-popup (timet + exit-intent)
     <nb-cookie>        samtykkebanner + Shopify Customer Privacy API

   VIGTIGT — ingen tekst i denne fil.
   Prototypen skrev sin egen danske markup med inline styles. I temaet
   renderes ALT synligt indhold i Liquid (locale-noegler + section settings)
   og slottes ind i light-DOM. Komponenterne styrer kun tilstand, visning
   og tilgaengelighed. Alle labels kommer fra attributter/markup.

   Ingen frameworks. Ingen build-step. Idempotent: filen kan indlaeses to
   gange, og elementerne kan re-monteres af Section Rendering API'et.
   ========================================================================== */
(function () {
  'use strict';

  /* ======================================================================
     0) Faelles hjaelpere
     ====================================================================== */

  var FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var SCROLL_LOCK_CLASS = 'nb-scroll-lock';
  var scrollLocks = 0;

  function lockScroll() {
    scrollLocks++;
    if (scrollLocks === 1 && document.body) {
      document.body.classList.add(SCROLL_LOCK_CLASS);
    }
  }

  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0 && document.body) {
      document.body.classList.remove(SCROLL_LOCK_CLASS);
    }
  }

  function focusableIn(root) {
    if (!root) return [];
    var nodes = root.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.offsetWidth || el.offsetHeight || el.getClientRects().length) out.push(el);
    }
    return out;
  }

  /* Focus trap. Returnerer en release-funktion der ogsaa giver fokus tilbage. */
  function trapFocus(root, opts) {
    var options = opts || {};
    var previous = document.activeElement;

    function onKeydown(e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      var items = focusableIn(root);
      if (!items.length) {
        e.preventDefault();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !root.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }

    root.addEventListener('keydown', onKeydown);

    var target = options.initialFocus || focusableIn(root)[0] || root;
    if (target) {
      if (target === root && !root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
    }

    return function release(restore) {
      root.removeEventListener('keydown', onKeydown);
      if (restore !== false && previous && typeof previous.focus === 'function' && document.contains(previous)) {
        try {
          previous.focus({ preventScroll: true });
        } catch (e) {
          previous.focus();
        }
      }
    };
  }

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  function storageRemove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      /* private mode */
    }
  }

  function emit(target, name, detail) {
    try {
      target.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
    } catch (e) {
      /* aeldre browsere uden CustomEvent-konstruktoer */
    }
  }

  function attr(el, name, fallback) {
    var value = el.getAttribute(name);
    return value === null || value === '' ? fallback : value;
  }

  function intAttr(el, name, fallback) {
    var n = parseInt(el.getAttribute(name), 10);
    return isNaN(n) ? fallback : n;
  }

  function boolAttr(el, name, fallback) {
    var value = el.getAttribute(name);
    if (value === null) return fallback;
    return value !== 'false' && value !== '0';
  }

  /* Saetter tekst omkring et {token}: returnerer [foer, efter]. */
  function splitToken(text, token) {
    var idx = text.indexOf(token);
    if (idx < 0) return [text, ''];
    return [text.slice(0, idx), text.slice(idx + token.length)];
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Viser et element og saetter .is-open, saa CSS-transitionen koerer.
     Reflowet er synkront med vilje: requestAnimationFrame koerer IKKE i en
     baggrundsfane, og saa ville elementet blive haengende usynligt. */
  function reveal(el) {
    el.hidden = false;
    /* eslint-disable-next-line no-unused-expressions */
    el.offsetHeight;
    el.classList.add('is-open');
  }

  /* ======================================================================
     1) <nb-countdown> — bestil-inden-nedtaelling
     ----------------------------------------------------------------------
     Taeller ned til fragt-cutoff (default kl. 14) paa hverdage og viser
     estimeret leveringsdato. Efter cutoff — eller i weekenden — rykkes
     afsendelsen til naeste hverdag.

     Attributter (alle labels SKAL saettes fra Liquid):
       cutoff          Time (0-23) for cutoff.            Default 14
       cutoff-minutes  Minut for cutoff.                  Default 0
       delivery-days   Hverdage fra afsendelse til levering. Default 2
       locale          BCP47-tag til datoformatering.     Default da-DK
       date-format     Skabelon m. {weekday} {day} {month} {year}
                       Default "{weekday} {day}. {month}"
       label-prefix    Tekst FOER nedtaellingen.
       label-today     Tekst EFTER nedtaellingen.
       label-next-day  Hele overskriften efter cutoff (hverdag).
       label-weekend   Hele overskriften i weekenden.
       label-delivery  Leveringslinje med {date}-token.
       icon-today      Emoji foer cutoff.                 Default 🚚
       icon-next       Emoji efter cutoff.                Default 📦
       icon-delivery   Emoji paa leveringslinjen.         Default 📅
       closed-days     Kommaliste med lukkedage (0=soendag). Default "0,6"
       theme           "light" (default) | "dark" → .nb-countdown--dark

     Markup der genereres (styles i CSS, ingen inline styles):
       .nb-countdown__row
         .nb-countdown__icon
         .nb-countdown__text
           .nb-countdown__head
             .nb-countdown__prefix / b.nb-countdown__timer / .nb-countdown__suffix
           .nb-countdown__deliver
             .nb-countdown__deliver-icon
             .nb-countdown__deliver-text (m. b.nb-countdown__date)

     Tidszone = browserens lokale tid, praecis som i prototypen.
     ====================================================================== */
  if (!customElements.get('nb-countdown')) {
    var NbCountdown = class NbCountdown extends HTMLElement {
      connectedCallback() {
        if (this._built) {
          this._start();
          return;
        }

        this._cutoffHour = intAttr(this, 'cutoff', 14);
        this._cutoffMin = intAttr(this, 'cutoff-minutes', 0);
        this._deliveryDays = intAttr(this, 'delivery-days', 2);
        this._locale = attr(this, 'locale', 'da-DK');
        this._dateFormat = attr(this, 'date-format', '{weekday} {day}. {month}');

        this._labels = {
          prefix: attr(this, 'label-prefix', ''),
          today: attr(this, 'label-today', ''),
          nextDay: attr(this, 'label-next-day', ''),
          weekend: attr(this, 'label-weekend', ''),
          delivery: attr(this, 'label-delivery', '{date}')
        };

        this._icons = {
          today: attr(this, 'icon-today', '🚚'),
          next: attr(this, 'icon-next', '📦'),
          delivery: attr(this, 'icon-delivery', '📅')
        };

        this._closedDays = attr(this, 'closed-days', '0,6')
          .split(',')
          .map(function (d) {
            return parseInt(d, 10);
          })
          .filter(function (d) {
            return !isNaN(d);
          });

        this._build();
        this._built = true;
        this._state = null;
        this._start();
      }

      disconnectedCallback() {
        this._stop();
      }

      _start() {
        this._stop();
        this._render();
        this._timer = window.setInterval(this._render.bind(this), 1000);
      }

      _stop() {
        if (this._timer) {
          window.clearInterval(this._timer);
          this._timer = null;
        }
      }

      _build() {
        this.textContent = '';
        this.classList.add('nb-countdown');
        if (attr(this, 'theme', 'light') === 'dark') this.classList.add('nb-countdown--dark');

        var row = document.createElement('span');
        row.className = 'nb-countdown__row';

        var icon = document.createElement('span');
        icon.className = 'nb-countdown__icon';
        icon.setAttribute('aria-hidden', 'true');

        var text = document.createElement('span');
        text.className = 'nb-countdown__text';

        var head = document.createElement('span');
        head.className = 'nb-countdown__head';

        var prefix = document.createElement('span');
        prefix.className = 'nb-countdown__prefix';

        var timer = document.createElement('b');
        timer.className = 'nb-countdown__timer';

        var suffix = document.createElement('span');
        suffix.className = 'nb-countdown__suffix';

        head.appendChild(prefix);
        head.appendChild(timer);
        head.appendChild(suffix);

        var deliver = document.createElement('span');
        deliver.className = 'nb-countdown__deliver';

        var deliverIcon = document.createElement('span');
        deliverIcon.className = 'nb-countdown__deliver-icon';
        deliverIcon.setAttribute('aria-hidden', 'true');
        deliverIcon.textContent = this._icons.delivery;

        var deliverText = document.createElement('span');
        deliverText.className = 'nb-countdown__deliver-text';

        var deliverBefore = document.createElement('span');
        var deliverDate = document.createElement('b');
        deliverDate.className = 'nb-countdown__date';
        var deliverAfter = document.createElement('span');

        deliverText.appendChild(deliverBefore);
        deliverText.appendChild(deliverDate);
        deliverText.appendChild(deliverAfter);

        deliver.appendChild(deliverIcon);
        deliver.appendChild(deliverText);

        text.appendChild(head);
        if (this._labels.delivery) text.appendChild(deliver);

        row.appendChild(icon);
        row.appendChild(text);
        this.appendChild(row);

        this._els = {
          icon: icon,
          prefix: prefix,
          timer: timer,
          suffix: suffix,
          deliverBefore: deliverBefore,
          deliverDate: deliverDate,
          deliverAfter: deliverAfter
        };
      }

      _isClosed(date) {
        return this._closedDays.indexOf(date.getDay()) !== -1;
      }

      _addBusinessDays(date, n) {
        var d = new Date(date.getTime());
        var guard = 0;
        while (n > 0 && guard < 400) {
          d.setDate(d.getDate() + 1);
          guard++;
          if (!this._isClosed(d)) n--;
        }
        return d;
      }

      _formatDate(date) {
        var parts = { weekday: '', day: String(date.getDate()), month: '', year: String(date.getFullYear()) };
        try {
          var fmt = new Intl.DateTimeFormat(this._locale, {
            weekday: 'short',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
          var list = fmt.formatToParts(date);
          for (var i = 0; i < list.length; i++) {
            if (parts.hasOwnProperty(list[i].type)) parts[list[i].type] = list[i].value;
          }
        } catch (e) {
          try {
            return date.toLocaleDateString(this._locale);
          } catch (e2) {
            return parts.day;
          }
        }
        return this._dateFormat.replace(/\{(weekday|day|month|year)\}/g, function (_, key) {
          return parts[key];
        });
      }

      _pad(n) {
        return String(n).length < 2 ? '0' + n : String(n);
      }

      _render() {
        var now = new Date();
        var cutoff = new Date(now.getTime());
        cutoff.setHours(this._cutoffHour, this._cutoffMin, 0, 0);

        var closedToday = this._isClosed(now);
        var beforeCutoff = !closedToday && now < cutoff;

        var ship = new Date(now.getTime());
        ship.setHours(0, 0, 0, 0);
        if (!beforeCutoff) ship = this._addBusinessDays(ship, 1);

        var deliveryDate = this._formatDate(this._addBusinessDays(ship, this._deliveryDays));
        var mode = beforeCutoff ? 'today' : closedToday ? 'weekend' : 'next';
        var stateKey = mode + '|' + deliveryDate;

        if (stateKey !== this._state) {
          this._state = stateKey;
          this._paint(mode, deliveryDate);
        }

        if (beforeCutoff) {
          var diff = cutoff - now;
          var h = Math.floor(diff / 3600000);
          var m = Math.floor((diff % 3600000) / 60000);
          var s = Math.floor((diff % 60000) / 1000);
          this._els.timer.textContent = this._pad(h) + ':' + this._pad(m) + ':' + this._pad(s);
        }
      }

      _paint(mode, deliveryDate) {
        var els = this._els;
        var labels = this._labels;

        this.classList.toggle('nb-countdown--open', mode === 'today');
        this.classList.toggle('nb-countdown--closed', mode !== 'today');

        if (mode === 'today') {
          els.icon.textContent = this._icons.today;
          els.prefix.textContent = labels.prefix;
          els.suffix.textContent = labels.today;
          els.timer.hidden = false;
        } else {
          els.icon.textContent = this._icons.next;
          els.prefix.textContent = mode === 'weekend' ? labels.weekend || labels.nextDay : labels.nextDay;
          els.suffix.textContent = '';
          els.timer.textContent = '';
          els.timer.hidden = true;
        }

        if (labels.delivery) {
          var parts = splitToken(labels.delivery, '{date}');
          els.deliverBefore.textContent = parts[0];
          els.deliverDate.textContent = deliveryDate;
          els.deliverAfter.textContent = parts[1];
        }
      }
    };

    customElements.define('nb-countdown', NbCountdown);
  }

  /* ======================================================================
     2) <nb-email-popup> — nyhedsbrev mod rabat
     ----------------------------------------------------------------------
     Vises timet OG ved exit-intent. Tilstand i localStorage 'nb_popup_v1'
     ("dismissed" | "joined"). Er noeglen sat, vises popup'en aldrig igen.

     Komponenten ejer INGEN markup. Liquid renderer indholdet i light-DOM:

       <nb-email-popup delay="14000">
         <div data-nb-popup hidden>
           <div data-nb-popup-overlay></div>
           <div data-nb-popup-box role="dialog" aria-modal="true"
                aria-labelledby="nb-popup-title">
             <button data-nb-popup-close aria-label="...">…</button>
             <div data-nb-popup-form>
               {% form 'customer' %} … <input type="email" name="contact[email]"> …
             </div>
             <div data-nb-popup-success hidden>… rabatkode + luk-knap …</div>
             <button data-nb-popup-dismiss>Nej tak …</button>
           </div>
         </div>
       </nb-email-popup>

     Kroge:
       [data-nb-popup]           rod, faar .is-open naar den vises
       [data-nb-popup-overlay]   klik = afvis (huskes)
       [data-nb-popup-box]       dialogboksen (focus trap)
       [data-nb-popup-close]     luk (huskes)
       [data-nb-popup-dismiss]   "nej tak" (huskes)
       [data-nb-popup-form]      skjules naar tilmeldingen er lykkedes
       [data-nb-popup-success]   vises naar tilmeldingen er lykkedes
       [data-nb-popup-done]      knap i success-panelet, lukker uden at huske
                                 (tilstanden er allerede "joined")

     Attributter:
       delay          ms foer timet visning.        Default 14000
       storage-key    localStorage-noegle.          Default nb_popup_v1
       exit-intent    "false" slaar exit-intent fra. Default true
       state          "joined" saettes fra Liquid naar
                      form.posted_successfully? — popup'en aabner da
                      direkte i success-tilstand.

     {% form 'customer' %} sender en almindelig POST og lander tilbage med
     ?customer_posted=true. Begge signaler behandles som "joined".
     ====================================================================== */
  if (!customElements.get('nb-email-popup')) {
    customElements.define(
      'nb-email-popup',
      class NbEmailPopup extends HTMLElement {
        connectedCallback() {
          this._root = this.querySelector('[data-nb-popup]');
          if (!this._root) return; /* intet indhold fra Liquid — goer intet */

          this._key = attr(this, 'storage-key', 'nb_popup_v1');
          this._delay = intAttr(this, 'delay', 14000);
          this._useExitIntent = boolAttr(this, 'exit-intent', true);
          this._box = this.querySelector('[data-nb-popup-box]') || this._root;
          this._open = false;
          this._release = null;

          this._bind();

          var posted = attr(this, 'state', '') === 'joined' || this._urlPosted();
          if (posted) {
            storageSet(this._key, 'joined');
            if (this.querySelector('[data-nb-popup-success]')) {
              this._showSuccess();
              this.show(true);
            }
            return;
          }

          if (storageGet(this._key)) return;

          this._timer = window.setTimeout(this.show.bind(this, false), this._delay);

          if (this._useExitIntent && window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
            this._onMouseout = function (e) {
              if (e.clientY <= 4 && !e.relatedTarget) this.show(false);
            }.bind(this);
            document.addEventListener('mouseout', this._onMouseout);
          }
        }

        disconnectedCallback() {
          if (this._timer) window.clearTimeout(this._timer);
          if (this._onMouseout) document.removeEventListener('mouseout', this._onMouseout);
          if (this._open) this._teardownOpenState(false);
        }

        _urlPosted() {
          try {
            return new URLSearchParams(window.location.search).get('customer_posted') === 'true';
          } catch (e) {
            return window.location.search.indexOf('customer_posted=true') !== -1;
          }
        }

        _bind() {
          var self = this;

          var overlay = this.querySelector('[data-nb-popup-overlay]');
          if (overlay) {
            overlay.addEventListener('click', function () {
              self.close(true);
            });
          }

          this._root.addEventListener('click', function (e) {
            if (e.target === self._root) self.close(true);
          });

          var closers = this.querySelectorAll('[data-nb-popup-close],[data-nb-popup-dismiss]');
          for (var i = 0; i < closers.length; i++) {
            closers[i].addEventListener('click', function (e) {
              e.preventDefault();
              self.close(true);
            });
          }

          var done = this.querySelectorAll('[data-nb-popup-done]');
          for (var j = 0; j < done.length; j++) {
            done[j].addEventListener('click', function (e) {
              e.preventDefault();
              self.close(false);
            });
          }

          this._onKeydown = function (e) {
            if (e.key === 'Escape' || e.keyCode === 27) self.close(true);
          };

          /* Tilmelding: {% form 'customer' %} poster siden. Vi markerer
             "joined" med det samme, saa popup'en ikke vender tilbage. */
          var form = this.querySelector('form');
          if (form) {
            form.addEventListener('submit', function () {
              if (form.checkValidity && !form.checkValidity()) return;
              storageSet(self._key, 'joined');
              emit(self, 'nb-popup-joined', null);
            });
          }
        }

        _showSuccess() {
          var formWrap = this.querySelector('[data-nb-popup-form]');
          var success = this.querySelector('[data-nb-popup-success]');
          if (formWrap) formWrap.hidden = true;
          if (success) success.hidden = false;
        }

        show(skipStorageCheck) {
          if (this._open || !this._root) return;
          if (!skipStorageCheck && storageGet(this._key)) return;
          this._open = true;

          if (this._timer) window.clearTimeout(this._timer);

          reveal(this._root);
          lockScroll();
          document.addEventListener('keydown', this._onKeydown);

          var email = this.querySelector('input[type="email"]');
          this._release = trapFocus(this._box, { initialFocus: email || null });

          emit(this, 'nb-popup-open', null);
        }

        close(remember) {
          if (!this._open) return;
          if (remember && !storageGet(this._key)) storageSet(this._key, 'dismissed');
          this._teardownOpenState(true);
          emit(this, 'nb-popup-close', { remembered: !!remember });
        }

        _teardownOpenState(animate) {
          this._open = false;
          document.removeEventListener('keydown', this._onKeydown);
          if (this._release) {
            this._release(true);
            this._release = null;
          }
          unlockScroll();

          this._root.classList.remove('is-open');
          var root = this._root;
          if (animate && !prefersReducedMotion()) {
            window.setTimeout(function () {
              if (!root.classList.contains('is-open')) root.hidden = true;
            }, 250);
          } else {
            root.hidden = true;
          }
        }

        /* Bruges af theme-editoren og evt. et "vis tilbud igen"-link. */
        reopen() {
          storageRemove(this._key);
          this.show(true);
        }
      }
    );
  }

  /* ======================================================================
     3) <nb-cookie> — samtykkebanner
     ----------------------------------------------------------------------
     Tilstand i localStorage 'nb_cookie_v1' ("all" | "necessary").
     Udsender 'nb-cookie-consent' (detail: { value, analytics, marketing,
     preferences }) paa baade elementet og window.

     Kobles til Shopifys Customer Privacy API:
       window.Shopify.customerPrivacy.setTrackingConsent(consent, cb)
     API'et findes ikke altid med det samme, saa det hentes defensivt via
     Shopify.loadFeatures og forsoeges igen et begraenset antal gange.

     Markup fra Liquid (komponenten ejer ingen tekst):
       <nb-cookie>
         <div data-nb-cookie hidden role="dialog" aria-labelledby="…">
           <p>… {{ 'nb.cookie.text' | t }} <a href="/policies/…">…</a></p>
           <button data-nb-cookie-accept>…</button>
           <button data-nb-cookie-necessary>…</button>
         </div>
       </nb-cookie>

     window.NBCookie.reopen() genaabner banneret (cookiepolitik-siden).
     ====================================================================== */
  if (!customElements.get('nb-cookie')) {
    var COOKIE_KEY = 'nb_cookie_v1';
    var PRIVACY_RETRIES = 12;
    var PRIVACY_DELAY = 400;

    function consentFor(value) {
      var granted = value === 'all';
      return { analytics: granted, marketing: granted, preferences: granted };
    }

    /* Sender samtykket videre til Shopify — helt defensivt. */
    function applyShopifyConsent(value, attempt) {
      var tries = attempt || 0;
      var consent = consentFor(value);
      var shopify = window.Shopify;

      if (shopify && shopify.customerPrivacy && typeof shopify.customerPrivacy.setTrackingConsent === 'function') {
        try {
          shopify.customerPrivacy.setTrackingConsent(consent, function () {});
        } catch (e) {
          /* API'et kan afvise kaldet — vi fejler stille. */
        }
        return;
      }

      if (shopify && typeof shopify.loadFeatures === 'function' && tries === 0) {
        try {
          shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
            if (error) return;
            applyShopifyConsent(value, 1);
          });
        } catch (e) {
          /* ignoreres */
        }
      }

      if (tries < PRIVACY_RETRIES) {
        window.setTimeout(function () {
          applyShopifyConsent(value, tries + 1);
        }, PRIVACY_DELAY);
      }
    }

    customElements.define(
      'nb-cookie',
      class NbCookie extends HTMLElement {
        connectedCallback() {
          this._panel = this.querySelector('[data-nb-cookie]');
          if (!this._panel) return; /* intet indhold fra Liquid */

          this._key = attr(this, 'storage-key', COOKIE_KEY);
          this._bind();

          window.NBCookie = window.NBCookie || {};
          window.NBCookie.reopen = this.reopen.bind(this);
          window.NBCookie.value = this.value.bind(this);
          window.NBCookie.choose = this.choose.bind(this);

          var stored = storageGet(this._key);
          if (stored) {
            applyShopifyConsent(stored, 0);
            this._panel.hidden = true;
            return;
          }

          this.open();
        }

        _bind() {
          var self = this;
          var accept = this.querySelectorAll('[data-nb-cookie-accept]');
          for (var i = 0; i < accept.length; i++) {
            accept[i].addEventListener('click', function (e) {
              e.preventDefault();
              self.choose('all');
            });
          }
          var necessary = this.querySelectorAll('[data-nb-cookie-necessary]');
          for (var j = 0; j < necessary.length; j++) {
            necessary[j].addEventListener('click', function (e) {
              e.preventDefault();
              self.choose('necessary');
            });
          }
        }

        value() {
          return storageGet(this._key);
        }

        open() {
          if (!this._panel) return;
          this._panel.classList.remove('is-out');
          reveal(this._panel);
        }

        choose(value) {
          var normalized = value === 'all' ? 'all' : 'necessary';
          storageSet(this._key, normalized);

          var detail = consentFor(normalized);
          detail.value = normalized;
          emit(this, 'nb-cookie-consent', detail);
          try {
            window.dispatchEvent(new CustomEvent('nb-cookie-consent', { detail: detail }));
          } catch (e) {
            /* ignoreres */
          }

          applyShopifyConsent(normalized, 0);
          this._hide();
        }

        _hide() {
          var panel = this._panel;
          if (!panel) return;
          panel.classList.remove('is-open');
          panel.classList.add('is-out');
          if (prefersReducedMotion()) {
            panel.hidden = true;
            panel.classList.remove('is-out');
            return;
          }
          window.setTimeout(function () {
            if (!panel.classList.contains('is-open')) {
              panel.hidden = true;
              panel.classList.remove('is-out');
            }
          }, 300);
        }

        reopen() {
          storageRemove(this._key);
          this.open();
        }
      }
    );
  }
})();
